import { Injectable, inject } from '@angular/core';
import { RegistrationApiDto, RegistrationDto } from './registration.model';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@htx/environment';
import { FileUploadService } from '../form/file-upload.service';

@Injectable({
  providedIn: 'root'
})
export class RegistrationService {
  private httpClient = inject(HttpClient);
  private fileUploadService = inject(FileUploadService);

  private readonly baseUrl = `${environment.backendUrl}/organizations`;

  private _registrationDto$ = new BehaviorSubject<RegistrationDto>(
    null as unknown as RegistrationDto
  );

  cache(dto: RegistrationDto) {
    this._registrationDto$.next(dto);
  }

  getCachedValue$(): Observable<RegistrationDto> {
    return this._registrationDto$.asObservable().pipe(
      map((dto) => {
        if (dto) {
          return dto;
        } else {
          throw Error('No cached value found');
        }
      })
    );
  }

  getCachedValue(): RegistrationDto {
    return this._registrationDto$.getValue();
  }

  isShortNameAvailable(shortName: string): Observable<boolean> {
    return this.httpClient
      .get<{ available: boolean }>(`${this.baseUrl}/short-name-available`, {
        params: new HttpParams().set('value', shortName)
      })
      .pipe(map((response) => response.available));
  }

  register(dto: RegistrationDto) {
    const formData = new FormData();
    formData.append('file', dto.document);
    const data: RegistrationApiDto = {
      legalName: dto.legalName,
      shortName: dto.shortName,
      sector: dto.sector,
      legalForm: dto.legalForm,
      website: dto.website,
      description: dto.description,
      foundingDate: dto.foundingDate,
      country: 'DE',
      postalCode: dto.postalCode,
      city: dto.city,
      street: dto.street,
      houseNumber: dto.houseNumber,
      commercialRegisterNumber: dto.commercialRegisterNumber,
      commercialRegisterCourt: dto.commercialRegisterCourt,
      taxIdNumber: dto.taxIdNumber,
      title: dto.title,
      firstName: dto.firstName,
      lastName: dto.lastName,
      position: dto.position,
      phoneNumber: dto.phoneNumber,
      email: dto.email,
      roles: ['ORGANIZATION_REPRESENTATIVE'],
      hasAgreedToDataProtectionDeclaration: dto.hasAgreedToDataProtectionDeclaration,
      hasAgreedToMembershipDeclaration: dto.hasAgreedToMembershipDeclaration
    };

    formData.append(
      'organizationRegistrationDto',
      new Blob([JSON.stringify(data)], {
        type: 'application/json'
      })
    );

    this.fileUploadService.cache(null as unknown as File);

    return this.httpClient.post(`${this.baseUrl}`, formData);
  }
}
